import { Select } from "@fluentui/web-components";
import { css, html, LitElement } from "lit";
import { customElement, property, query, queryAssignedElements } from "lit/decorators.js";

//This component is a wrapper around the Fluent UI Select component that directly renders the selected option.
//This allows for the selected option to be rendered with icons and other custom content, rather than just text content.
@customElement("patched-fluent-select")
export class PatchedFluentSelect extends LitElement {
    static styles = css`
        :host {
            display: block;
            position: relative;
        }

        fluent-select {
            width: 100%;
        }

        .selected-option {
            position: absolute;
            inset: 0;
            display: flex;
            align-items: center;
            pointer-events: none;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
            padding-inline: 10px;
        }

        .selected-option[hidden] {
            display: none;
        }

        .selected-option :is(div, span) {
            display: inline-flex;
            align-items: center;
            gap: 0.25rem;
        }

        fluent-select::part(selected-value) {
            color: transparent;
        }
    `;

    @property({ type: String })
    value = "";

    @property({ type: Boolean }) disabled?: boolean;

    @query("fluent-select")
    private fluentSelect?: Select;

    @query(".selected-option")
    private selectedOptionContainer?: HTMLDivElement;

    @queryAssignedElements({ selector: "fluent-option", flatten: true })
    private fluentOptions!: HTMLElement[];

    firstUpdated() {
        if (!this.value && this.fluentSelect) {
            this.value = this.fluentSelect.value;
        }

        this.syncSelectedOption();
    }

    updated(changedProperties: Map<string, unknown>) {
        if (changedProperties.has("value")) {
            if (this.fluentSelect && this.fluentSelect.value !== this.value) {
                this.fluentSelect.value = this.value;
            }

            this.syncSelectedOption();
        }
    }

    private onFluentSelectChange = (event: Event) => {
        event.stopPropagation();

        const select = event.currentTarget as Select;
        this.value = select.value;
        this.syncSelectedOption();

        this.dispatchEvent(new Event("change", { bubbles: true, composed: true }));
    };

    private syncSelectedOption() {
        if (!this.selectedOptionContainer) return;

        this.selectedOptionContainer.replaceChildren();

        const selectedOption = this.fluentOptions.find(option => {
            const optionValue = option.getAttribute("value") ?? "";
            return optionValue === this.value;
        });

        if (!selectedOption) {
            this.selectedOptionContainer.hidden = true;
            return;
        }

        this.selectedOptionContainer.hidden = false;
        const fragments = Array.from(selectedOption.childNodes).map(node => node.cloneNode(true));
        this.selectedOptionContainer.append(...fragments);
    }

    render() {
        return html`
            <fluent-select
                .value=${this.value}
                @change=${this.onFluentSelectChange}
                ?disabled=${this.disabled}>
                <slot @slotchange=${this.syncSelectedOption}></slot>
            </fluent-select>
            <div class="selected-option" hidden aria-hidden="true"></div>
        `;
    }
}